// Pure helpers for the Competitor Benchmark page: wording, dates, the gap
// sentence, evidence links, and the form's draft to request mapping. No
// figure is made up here; every number passed in comes from the API.
import type { BenchmarkBoard, Campaign, CampaignStatus, Channel, SignalPoint } from "@dashboard-intelligent/api/src/modules/benchmark/types";
import type { MetricFormat } from "@dashboard-intelligent/api/src/modules/industry/types";
import { formatMetric } from "../../format";
import type { CampaignInput } from "./api";

export const STATUS_TEXT: Record<CampaignStatus, string> = {
  running: "Berjalan",
  ended: "Selesai",
  planned: "Direncanakan",
};

export const STATUS_ORDER: CampaignStatus[] = ["running", "planned", "ended"];

/** What a null engagement says on screen. Not recorded is not zero. */
export const NOT_RECORDED = "Tidak dicatat";

/** Channel labels as the API names them in byChannel, so the page never keeps its own copy. */
export function channelLabels(board: BenchmarkBoard): Record<string, string> {
  return Object.fromEntries(board.byChannel.map((c) => [c.channel, c.label]));
}

export function channelText(channel: Channel, labels: Record<string, string>): string {
  return labels[channel] ?? channel;
}

const DATE = new Intl.DateTimeFormat("id-ID", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });

/** "2026-01-15" as "15 Jan 2026". Anything unparsable is shown as written. */
export function formatDate(iso: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) return iso;
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  return Number.isNaN(date.getTime()) ? iso : DATE.format(date);
}

/** The campaign's run, in words. */
export function periodText(campaign: Pick<Campaign, "startedOn" | "endedOn" | "status">): string {
  const start = formatDate(campaign.startedOn);
  if (campaign.endedOn) return `${start} sampai ${formatDate(campaign.endedOn)}`;
  return campaign.status === "planned" ? `rencana mulai ${start}` : `mulai ${start}, tanggal selesai belum dicatat`;
}

export function engagementText(engagement: number | null): string {
  return engagement === null ? NOT_RECORDED : formatMetric(engagement, "count");
}

/** Evidence is a link only when it is a plain http or https URL; anything else is text. */
export function evidenceUrl(evidence: string): string | null {
  const value = evidence.trim();
  if (!/^https?:\/\/\S+$/i.test(value)) return null;
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:" ? url.href : null;
  } catch {
    return null;
  }
}

/** "A", "A dan B", "A, B, dan C". */
export function joinWords(items: string[]): string {
  if (items.length <= 1) return items[0] ?? "";
  if (items.length === 2) return `${items[0]} dan ${items[1]}`;
  return `${items.slice(0, -1).join(", ")}, dan ${items[items.length - 1]}`;
}

export function listText(items: string[], none = "Belum ada"): string {
  return items.length > 0 ? items.join(", ") : none;
}

/** Formats where a difference reads as points (percentage points, index points). */
const POINT_FORMATS: MetricFormat[] = ["percent", "decimal", "score"];

/** The size of a gap in the signal's own unit, without its sign. */
export function gapSize(gap: number, format: MetricFormat): string {
  const size = Math.abs(gap);
  return POINT_FORMATS.includes(format) ? `${formatMetric(size, "decimal")} poin` : formatMetric(size, format);
}

/** A gap with its direction in words, so the meaning never rests on a sign. */
export function gapText(gap: number, format: MetricFormat): string {
  if (gap === 0) return "sama dengan pesaing";
  return `${gapSize(gap, format)} ${gap > 0 ? "di atas" : "di bawah"} pesaing`;
}

/** Where own stands against the competitor, in words. */
export function directionText(gap: number): string {
  return gap > 0 ? "di atas pesaing" : gap < 0 ? "di bawah pesaing" : "sama dengan pesaing";
}

/** A gap for a tile: signed with a plain hyphen-minus. */
export function signedGap(gap: number, format: MetricFormat): string {
  const sign = gap > 0 ? "+" : gap < 0 ? "-" : "";
  return `${sign}${gapSize(gap, format)}`;
}

/** Campaigns, most recently recorded first (the API appends new ones). */
export function newestFirst(campaigns: Campaign[]): Campaign[] {
  return [...campaigns].reverse();
}

// ---------------------------------------------------------------------------
// The figures and chart data of the bento. Every count here is a count of
// rows the API sent; nothing is estimated.

export interface Part {
  label: string;
  value: number;
}

/** One competitor's recorded campaigns, counted per status, under its short axis name. */
export type StatusCounts = { label: string; name: string; total: number } & Record<CampaignStatus, number>;

/**
 * Recorded campaigns per competitor and status, in the board's own order of
 * competitors. `label` is the API's short name, made for an axis tick.
 */
export function statusByCompetitor(board: BenchmarkBoard): StatusCounts[] {
  return board.competitors.map((competitor) => {
    const own = board.campaigns.filter((c) => c.competitorId === competitor.id);
    const of = (status: CampaignStatus) => own.filter((c) => c.status === status).length;
    return { label: competitor.short, name: competitor.name, total: own.length, running: of("running"), planned: of("planned"), ended: of("ended") };
  });
}

/**
 * Campaign counts per focus tag, largest first. A campaign counts once per
 * tag it carries; ties keep the order the tags were first recorded in.
 */
export function focusCounts(campaigns: Campaign[]): Part[] {
  const counts = new Map<string, number>();
  for (const campaign of campaigns) {
    const tags = new Set(campaign.focus.map((tag) => tag.trim()).filter((tag) => tag !== ""));
    for (const tag of tags) counts.set(tag, (counts.get(tag) ?? 0) + 1);
  }
  return [...counts.entries()].map(([label, value]) => ({ label, value })).sort((a, b) => b.value - a.value);
}

/** In how many groups own stands above, level with, or below the competitor. */
export interface Standing {
  above: number;
  level: number;
  below: number;
}

export function standingOf(points: SignalPoint[]): Standing {
  const standing: Standing = { above: 0, level: 0, below: 0 };
  for (const point of points) {
    if (point.own > point.competitor) standing.above += 1;
    else if (point.own < point.competitor) standing.below += 1;
    else standing.level += 1;
  }
  return standing;
}

/**
 * The standing as three columns, in a fixed order. One word each, so all
 * three ticks fit a narrow lane; the chart's title and sentence say that the
 * position is against the competitor.
 */
export function standingColumns(standing: Standing): Part[] {
  return [
    { label: "Atas", value: standing.above },
    { label: "Sama", value: standing.level },
    { label: "Bawah", value: standing.below },
  ];
}

/**
 * The standing as parts of the whole set of groups, for a donut: the three
 * counts sum to the number of groups. A position without a group is left out.
 */
export function standingParts(standing: Standing): Part[] {
  return [
    { label: "Di atas pesaing", value: standing.above },
    { label: "Sama dengan pesaing", value: standing.level },
    { label: "Di bawah pesaing", value: standing.below },
  ].filter((part) => part.value > 0);
}

/** Groups by the size of their gap, largest first, whichever side it falls on. */
export function largestGaps(points: SignalPoint[]): SignalPoint[] {
  return [...points].sort((a, b) => Math.abs(b.own - b.competitor) - Math.abs(a.own - a.competitor));
}

/**
 * The end of a scale that holds every value given. A percentage that stays
 * within 100 is read against 100; anything else rounds up to a 1, 2, or 5
 * step, so the end of the scale is a number a reader can name.
 */
export function scaleEnd(values: number[], format: MetricFormat): number {
  const top = Math.max(0, ...values);
  if (format === "percent" && top <= 100) return 100;
  if (top <= 0) return 1;
  const power = 10 ** Math.floor(Math.log10(top));
  for (const step of [1, 2, 5, 10]) {
    if (top <= step * power) return step * power;
  }
  return 10 * power;
}

/** A gap for a figure card: the signed number, and "poin" as its unit where a difference reads as points. */
export function gapParts(gap: number, format: MetricFormat): { value: string; unit?: string } {
  const sign = gap > 0 ? "+" : gap < 0 ? "-" : "";
  const size = Math.abs(gap);
  return POINT_FORMATS.includes(format) ? { value: `${sign}${formatMetric(size, "decimal")}`, unit: "poin" } : { value: `${sign}${formatMetric(size, format)}` };
}

/** A sentence opening: the first letter in upper case. */
export function sentence(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

// ---------------------------------------------------------------------------
// The record form

export interface CampaignDraft {
  competitorId: string;
  title: string;
  channel: string;
  status: CampaignStatus;
  startedOn: string;
  endedOn: string;
  angle: string;
  focus: string;
  evidence: string;
  recordedBy: string;
  engagement: string;
}

export type DraftField = keyof CampaignDraft;

export const EMPTY_DRAFT: CampaignDraft = {
  competitorId: "",
  title: "",
  channel: "",
  status: "running",
  startedOn: "",
  endedOn: "",
  angle: "",
  focus: "",
  evidence: "",
  recordedBy: "",
  engagement: "",
};

export type FieldErrors = Partial<Record<DraftField, string>>;

const REQUIRED: [DraftField, string][] = [
  ["competitorId", "Pilih pesaing."],
  ["title", "Isi judul kampanye."],
  ["channel", "Pilih kanal."],
  ["startedOn", "Isi tanggal mulai."],
  ["angle", "Isi pesan utama kampanye."],
  ["evidence", "Isi tautan atau catatan sumber pengamatan."],
  ["recordedBy", "Isi nama pencatat."],
];

/**
 * Turn the draft into the request body. Only empty required fields and an
 * engagement that is not a number are caught here; every other rule is the
 * server's, and its 400 messages land on the same fields.
 */
export function toInput(draft: CampaignDraft): { ok: true; input: CampaignInput } | { ok: false; errors: FieldErrors } {
  const errors: FieldErrors = {};
  for (const [field, message] of REQUIRED) {
    if (draft[field].trim() === "") errors[field] = message;
  }
  const engagementRaw = draft.engagement.trim();
  const engagement = engagementRaw === "" ? null : Number(engagementRaw);
  if (engagement !== null && !Number.isFinite(engagement)) errors.engagement = "Isi angka, atau kosongkan bila tidak dicatat.";
  if (Object.keys(errors).length > 0) return { ok: false, errors };
  return {
    ok: true,
    input: {
      competitorId: draft.competitorId,
      title: draft.title.trim(),
      channel: draft.channel as Channel,
      status: draft.status,
      startedOn: draft.startedOn.trim(),
      endedOn: draft.endedOn.trim() === "" ? null : draft.endedOn.trim(),
      angle: draft.angle.trim(),
      focus: draft.focus
        .split(",")
        .map((f) => f.trim())
        .filter((f) => f !== ""),
      evidence: draft.evidence.trim(),
      recordedBy: draft.recordedBy.trim(),
      // Empty stays null: not recorded, never zero.
      engagement,
    },
  };
}

const FIELDS = new Set<string>(Object.keys(EMPTY_DRAFT));

/** The form field a 400 detail path belongs to ("focus.0" is the focus field), or null for the whole form. */
export function fieldOf(path: string): DraftField | null {
  const head = path.split(".")[0] ?? "";
  return FIELDS.has(head) ? (head as DraftField) : null;
}

/**
 * The server's validation messages in Indonesian where the wording is a
 * known validator message; anything else is shown exactly as sent.
 */
export function issueText(message: string): string {
  if (message === "Required") return "Wajib diisi.";
  if (/^String must contain at least 1 character/.test(message)) return "Wajib diisi.";
  const max = /^String must contain at most (\d+) character/.exec(message);
  if (max) return `Paling banyak ${max[1]} karakter.`;
  if (message === "Expected integer, received float") return "Isi bilangan bulat.";
  if (/^Number must be greater than or equal to 0/.test(message)) return "Tidak boleh kurang dari 0.";
  if (/^Invalid enum value/.test(message)) return "Pilih salah satu pilihan yang tersedia.";
  return message;
}
